import { Fragment, type JSX, type KeyboardEvent, type ReactNode } from "react";
import { LockClosedRegular } from "@fluentui/react-icons";

/** One of the three numeric columns in the compact hours table. */
export interface HoursTableColumn {
  /** Column header text, e.g. "M–F". */
  short: string;
  /** Full field label, surfaced as the header tooltip. */
  full: string;
}

/** A deal row in the compact hours table. */
export interface HoursTableRow {
  /** React list key — unique even when ids collide (e.g. expired PU&I rows share TaskId 0). */
  key: string;
  /** Id handed back to the change callbacks; rows with colliding ids must not be editable. */
  id: number;
  name: string;
  /** Muted line under the deal name (company / group / status). */
  context?: string;
  /** Shows a lock icon next to the deal name. */
  locked?: boolean;
  values: readonly [string, string, string];
  editable: readonly [boolean, boolean, boolean];
  ariaLabels: readonly [string, string, string];
  /** Rendered in a full-width row under the deal row (lock notes, row totals, validation). */
  notes?: ReactNode;
}

export interface HoursTableProps {
  ariaLabel: string;
  /** First column header; "Deal" unless the rows are people (e.g. bankers). */
  nameHeader?: string;
  columns: readonly [HoursTableColumn, HoursTableColumn, HoursTableColumn];
  rows: HoursTableRow[];
  /** Preformatted totals for the footer row. */
  totals: readonly [string, string, string];
  integerOnly?: boolean;
  onValueChange: (rowId: number, index: 0 | 1 | 2, value: string) => void;
  onValueBlur?: (rowId: number, index: 0 | 1 | 2, value: string) => void;
}

// Whole-hour forms mirror the Web grid, which blocks decimal separators and
// sign keys at the keyboard level.
const NON_INTEGER_KEYS = new Set([".", ",", "e", "E", "+", "-"]);

function blockNonIntegerKeys(event: KeyboardEvent<HTMLInputElement>): void {
  if (NON_INTEGER_KEYS.has(event.key)) event.preventDefault();
}

const FIELD_INDEXES = [0, 1, 2] as const;

/** Compact 4-column hours grid: Deal | first | second | third, with a totals footer. */
export function HoursTable({
  ariaLabel,
  nameHeader = "Deal",
  columns,
  rows,
  totals,
  integerOnly = false,
  onValueChange,
  onValueBlur,
}: HoursTableProps): JSX.Element {
  return (
    <div className="weekly-hours__table-wrap">
      <table className="weekly-hours__table" aria-label={ariaLabel}>
        <thead>
          <tr>
            <th scope="col">{nameHeader}</th>
            {columns.map((column) => (
              <th key={column.short} scope="col" title={column.full}>
                {column.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Fragment key={row.key}>
              <tr
                className={
                  row.notes ? "weekly-hours__deal-row weekly-hours__deal-row--noted" : "weekly-hours__deal-row"
                }
              >
                <th scope="row" className="weekly-hours__deal-cell">
                  <span className="weekly-hours__deal-name" title={row.name}>
                    {row.name}
                    {row.locked ? <LockClosedRegular aria-label="Entry locked" /> : null}
                  </span>
                  {row.context ? <span className="weekly-hours__deal-context">{row.context}</span> : null}
                </th>
                {FIELD_INDEXES.map((index) => (
                  <td key={index}>
                    <input
                      type="number"
                      inputMode={integerOnly ? "numeric" : "decimal"}
                      min="0"
                      step={integerOnly ? "1" : "any"}
                      placeholder="–"
                      value={row.values[index]}
                      disabled={!row.editable[index]}
                      aria-label={row.ariaLabels[index]}
                      onChange={(event) => onValueChange(row.id, index, event.target.value)}
                      onKeyDown={integerOnly ? blockNonIntegerKeys : undefined}
                      onBlur={onValueBlur ? (event) => onValueBlur(row.id, index, event.target.value) : undefined}
                    />
                  </td>
                ))}
              </tr>
              {row.notes ? (
                <tr className="weekly-hours__note-row">
                  <td colSpan={4}>{row.notes}</td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            {totals.map((total, index) => (
              <td key={index}>{total}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
